import { router } from "@inertiajs/react"
import {
  CalendarIcon,
  ClockIcon,
  Edit2Icon,
  GlobeIcon,
  LinkIcon,
  MoreHorizontalIcon,
  PlusIcon,
  RepeatIcon,
  SendIcon,
  Trash2Icon,
  XCircleIcon,
} from "lucide-react"
import { type ReactNode, useState } from "react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { AdminLayout } from "@/layouts/AdminLayout"
import type { EventFilters, EventRow, PoiOption } from "@/lib/types"
import { EventForm } from "./Form"

interface PropertySummary {
  id: number
  name: string
  region_id: number | null
}

interface Props {
  property: PropertySummary
  private_events: EventRow[]
  public_events: EventRow[]
  pois: PoiOption[]
  filters: EventFilters
  urls: { index: string; create: string; submit_public: string }
}

/* ------------------------------------------------------------------ */
/*  Status badge styling                                              */
/* ------------------------------------------------------------------ */

function statusBadgeVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "approved":
      return "default"
    case "pending":
    case "needs_changes":
      return "secondary"
    case "rejected":
      return "destructive"
    default:
      return "outline"
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case "needs_changes":
      return "Needs changes"
    default:
      return status.charAt(0).toUpperCase() + status.slice(1)
  }
}

/* ------------------------------------------------------------------ */
/*  Date/time formatting helpers                                      */
/* ------------------------------------------------------------------ */

function formatEventDate(isoString: string): string {
  const date = new Date(isoString)
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  })
}

function formatEventTime(isoString: string): string {
  const date = new Date(isoString)
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  })
}

function isEventPast(isoString: string): boolean {
  return new Date(isoString) < new Date()
}

/* ------------------------------------------------------------------ */
/*  Confirmation dialog state                                         */
/* ------------------------------------------------------------------ */

interface ConfirmState {
  open: boolean
  title: string
  description: string
  action: string
  variant: "default" | "destructive"
  onConfirm: () => void
}

const CONFIRM_INITIAL: ConfirmState = {
  open: false,
  title: "",
  description: "",
  action: "",
  variant: "default",
  onConfirm: () => {},
}

/* ------------------------------------------------------------------ */
/*  Event card                                                        */
/* ------------------------------------------------------------------ */

interface EventCardProps {
  event: EventRow
  onEdit: (e: EventRow) => void
  onCancel: (e: EventRow) => void
  onDelete: (e: EventRow) => void
  readOnly?: boolean
}

function EventCard({ event, onEdit, onCancel, onDelete, readOnly = false }: EventCardProps) {
  const cancelled = Boolean(event.cancelled_at)
  const past = isEventPast(event.starts_at)

  return (
    <Card
      className={[
        "gap-3 py-4 transition-colors",
        cancelled ? "border-dashed opacity-60" : "",
        past && !cancelled ? "bg-muted/30" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <CardHeader className="pb-0">
        {/* Date/time — primary information, always visible at top */}
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <CalendarIcon className="size-3" />
          <span>{formatEventDate(event.starts_at)}</span>
          <span aria-hidden="true">at</span>
          <ClockIcon className="size-3" />
          <span>{formatEventTime(event.starts_at)}</span>
          {past && !cancelled && (
            <Badge variant="outline" className="ml-1 text-[10px] leading-none">
              Past
            </Badge>
          )}
        </div>

        {/* Title + badges row */}
        <div className="flex items-start justify-between gap-2">
          <CardTitle
            className={[
              "text-base leading-snug",
              cancelled ? "line-through text-muted-foreground" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {event.name}
          </CardTitle>

          {!readOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 shrink-0"
                  aria-label={`Actions for ${event.name}`}
                >
                  <MoreHorizontalIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(event)}>
                  <Edit2Icon className="mr-2 size-4" />
                  Edit
                </DropdownMenuItem>
                {!cancelled && (
                  <DropdownMenuItem onClick={() => onCancel(event)}>
                    <XCircleIcon className="mr-2 size-4" />
                    Cancel event
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onDelete(event)}
                >
                  <Trash2Icon className="mr-2 size-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Linked POI */}
        {event.poi_name && (
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <LinkIcon className="size-3" />
            At {event.poi_name}
          </p>
        )}
      </CardHeader>

      <CardContent className="pb-0">
        {event.description && (
          <p className="mb-2 line-clamp-2 text-sm text-muted-foreground">
            {event.description}
          </p>
        )}
        <div className="flex flex-wrap gap-1.5">
          {event.recurrence?.frequency && (
            <Badge variant="secondary" className="text-xs capitalize">
              <RepeatIcon className="mr-1 size-3" />
              {event.recurrence.frequency}
            </Badge>
          )}
          {cancelled && (
            <Badge variant="destructive" className="text-xs">
              Cancelled
            </Badge>
          )}
        </div>
      </CardContent>

      <CardFooter className="pt-0">
        <Badge variant={statusBadgeVariant(event.status)} className="text-xs">
          {statusLabel(event.status)}
        </Badge>
      </CardFooter>
    </Card>
  )
}

/* ------------------------------------------------------------------ */
/*  Filter bar                                                        */
/* ------------------------------------------------------------------ */

interface FilterBarProps {
  filters: EventFilters
  indexUrl: string
}

function FilterBar({ filters, indexUrl }: FilterBarProps) {
  return (
    <div className="flex items-center gap-3">
      <Select
        value={filters.scope ?? "all"}
        onValueChange={(v) =>
          router.get(
            indexUrl,
            { scope: v === "all" ? undefined : v },
            { preserveState: true, replace: true },
          )
        }
      >
        <SelectTrigger className="h-8 w-44 text-sm">
          <SelectValue placeholder="All events" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All events</SelectItem>
          <SelectItem value="upcoming">Upcoming only</SelectItem>
          <SelectItem value="past">Past events</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Page component                                                    */
/* ------------------------------------------------------------------ */

export default function PropertyEventsIndex({
  property,
  private_events,
  public_events,
  pois,
  filters,
  urls,
}: Props) {
  const [formOpen, setFormOpen] = useState(false)
  const [editingEvent, setEditingEvent] = useState<EventRow | null>(null)
  const [publicFormOpen, setPublicFormOpen] = useState(false)
  const [confirm, setConfirm] = useState<ConfirmState>(CONFIRM_INITIAL)

  function openCreate() {
    setEditingEvent(null)
    setFormOpen(true)
  }

  function openEdit(event: EventRow) {
    setEditingEvent(event)
    setFormOpen(true)
  }

  function closeForm() {
    setFormOpen(false)
    setEditingEvent(null)
  }

  function handleDelete(event: EventRow) {
    setConfirm({
      open: true,
      title: `Delete "${event.name}"?`,
      description:
        "This event will be permanently removed. This action cannot be undone.",
      action: "Delete",
      variant: "destructive",
      onConfirm: () => {
        router.delete(event.delete_url!)
        setConfirm(CONFIRM_INITIAL)
      },
    })
  }

  function handleCancel(event: EventRow) {
    setConfirm({
      open: true,
      title: `Cancel "${event.name}"?`,
      description:
        "The event will be marked as cancelled and hidden from guest guides. This cannot be undone.",
      action: "Cancel event",
      variant: "destructive",
      onConfirm: () => {
        router.patch(event.cancel_url!)
        setConfirm(CONFIRM_INITIAL)
      },
    })
  }

  return (
    <TooltipProvider>
      <div>
        {/* Page header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Events</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">{property.name}</p>
          </div>
          <div className="flex gap-2">
            {property.region_id && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" variant="outline" onClick={() => setPublicFormOpen(true)}>
                    <SendIcon className="mr-1.5 size-4" />
                    Submit Public Event
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Submit an event for moderation review. Once approved, it will be visible to all properties in your region.
                </TooltipContent>
              </Tooltip>
            )}
            <Button size="sm" onClick={openCreate}>
              <PlusIcon className="mr-1.5 size-4" />
              Add Event
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="private">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <TabsList>
              <TabsTrigger value="private">
                Your Events
                {private_events.length > 0 && (
                  <Badge variant="secondary" className="ml-1.5 h-5 min-w-5 px-1.5 text-[10px]">
                    {private_events.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="public">
                <GlobeIcon className="mr-1.5 size-3.5" />
                Regional Events
                {public_events.length > 0 && (
                  <Badge variant="secondary" className="ml-1.5 h-5 min-w-5 px-1.5 text-[10px]">
                    {public_events.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <FilterBar filters={filters} indexUrl={urls.index} />
          </div>

          <TabsContent value="private">
            {private_events.length === 0 ? (
              <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
                <CalendarIcon className="mx-auto mb-3 size-8 opacity-30" />
                <p className="text-sm font-medium">No events yet</p>
                <p className="mx-auto mt-1 max-w-xs text-xs">
                  Create events like breakfast hours, live music nights, or local festivals to include in your guest guides.
                </p>
                <Button variant="default" size="sm" onClick={openCreate} className="mt-4">
                  <PlusIcon className="mr-1.5 size-4" />
                  Create your first event
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {private_events.map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onEdit={openEdit}
                    onCancel={handleCancel}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="public">
            {public_events.length === 0 ? (
              <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
                <GlobeIcon className="mx-auto mb-3 size-8 opacity-30" />
                <p className="text-sm font-medium">
                  {property.region_id
                    ? "No regional events available"
                    : "No region assigned"}
                </p>
                <p className="mx-auto mt-1 max-w-xs text-xs">
                  {property.region_id
                    ? "Regional events are shared across all properties in your area. Submit one to get started."
                    : "Your property needs a region assignment before you can see or submit regional events."}
                </p>
                {property.region_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPublicFormOpen(true)}
                    className="mt-4"
                  >
                    <SendIcon className="mr-1.5 size-4" />
                    Submit a public event
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {public_events.map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onEdit={openEdit}
                    onCancel={handleCancel}
                    onDelete={handleDelete}
                    readOnly
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Private event form sheet */}
        <Sheet open={formOpen} onOpenChange={(open) => !open && closeForm()}>
          <SheetContent className="overflow-y-auto sm:max-w-lg">
            <SheetHeader>
              <SheetTitle>{editingEvent ? "Edit Event" : "Add Event"}</SheetTitle>
            </SheetHeader>
            {formOpen && (
              <EventForm
                event={editingEvent ?? undefined}
                pois={pois}
                mode="private"
                createUrl={urls.create}
                submitPublicUrl={urls.submit_public}
                onClose={closeForm}
              />
            )}
          </SheetContent>
        </Sheet>

        {/* Public event submission sheet */}
        <Sheet open={publicFormOpen} onOpenChange={(open) => !open && setPublicFormOpen(false)}>
          <SheetContent className="overflow-y-auto sm:max-w-lg">
            <SheetHeader>
              <SheetTitle>Submit Public Event</SheetTitle>
            </SheetHeader>
            {publicFormOpen && (
              <EventForm
                pois={pois}
                mode="submit_public"
                createUrl={urls.create}
                submitPublicUrl={urls.submit_public}
                onClose={() => setPublicFormOpen(false)}
              />
            )}
          </SheetContent>
        </Sheet>

        {/* Shared confirmation dialog */}
        <AlertDialog
          open={confirm.open}
          onOpenChange={(open) => !open && setConfirm(CONFIRM_INITIAL)}
        >
          <AlertDialogContent size="sm">
            <AlertDialogHeader>
              <AlertDialogTitle>{confirm.title}</AlertDialogTitle>
              <AlertDialogDescription>{confirm.description}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep it</AlertDialogCancel>
              <AlertDialogAction variant={confirm.variant} onClick={confirm.onConfirm}>
                {confirm.action}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  )
}

PropertyEventsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
